import * as React from 'react';

import ActivitiesCell from './activitiesCell';
import Donut from './donut';
import { EDIT_ACTIVITIES } from './packActionModals';
import PackActions from './packActions';
import ProgressHelp from './progressHelp';
import { ALL_CARD_PARTS, ASSIGNED_ON, CardPart, LAST_ACTIVE, LEARNING_TOOLS, STUDENTS } from './useShownCardParts';
import { StudentCompletionBadges, completedCountFor } from './studentCompletionBadges';
import {
  ALL,
  AggregatedProgress,
  CLASSIFICATIONS,
  Pack,
  PackActivity,
  PackClassroom,
  RecommendationCounts,
  StudentCompletion,
  UnitProgress,
  aggregateProgress,
  canManagePack,
  classAssignmentLine,
  classAssignmentSentence,
  escapeHtml,
  formatDate,
  formatShortDate,
  isDefaultPackName,
  manageStudentsLink,
  nameMatchesSearch,
  packAssignedEpochFor,
  packCardId,
  rosterCountFor,
  selectedClassroomFor,
  studentCompletionsFor,
  studentOverviewLink,
} from './shared';

export { StudentCompletionBadges, completedCountFor } from './studentCompletionBadges';

import { assignedClassrooms } from '../classroom_assignment';
import { EXTRA_SMALL_ICON_BASE_SRC, Tooltip, pluralize, safeStripHtml } from '../../../../Shared/index';

const VISIBLE_ACTIVITY_COUNT = 3

interface PackCardProps {
  pack: Pack;
  unitProgress?: UnitProgress;
  recommendationCounts: RecommendationCounts;
  selectedClassroomId: string | number;
  onPackChanged: (snackbarCopy?: string) => void;
  isBoosted?: boolean;
  onToggleBoost?: () => void;
  parentDiagnosticHref?: string;
  parentDiagnosticLabel?: string;
  isCompact?: boolean;
  activitySearch?: string;
  shownParts?: CardPart[];
}

// recommendation packs are auto-named "<Skill> (<Diagnostic> Recommendations)"
const RECOMMENDATION_NAME_PATTERN = /^(.*\S)\s*\((.+?)\s+Recommendations?\)\s*$/i

export function splitRecommendationName(unitName: string, fromRecommendations: boolean): { title: string, parentDiagnostic: string | null } {
  const match = fromRecommendations ? unitName.match(RECOMMENDATION_NAME_PATTERN) : null
  if (!match) { return { title: unitName, parentDiagnostic: null } }
  return { title: match[1], parentDiagnostic: match[2] }
}

// "ELL Starter Post" → family "ELL Starter Diagnostic" + phase "Post"
export function parseParentDiagnostic(raw: string): { family: string, phase: 'Pre' | 'Post' | null } {
  const normalized = raw.replace(/Baseline/gi, 'Pre').replace(/Growth/gi, 'Post')
  const phaseMatch = normalized.match(/\s*\b(Pre|Post)\b\s*$/i)
  const phase = phaseMatch ? (phaseMatch[1][0].toUpperCase() + phaseMatch[1].slice(1).toLowerCase()) as 'Pre' | 'Post' : null
  let family = (phase ? normalized.slice(0, phaseMatch.index) : normalized).trim()
  if (family && !/diagnostic$/i.test(family)) { family = `${family} Diagnostic` }
  return { family, phase }
}

const ToolChipIcon = ({ classificationKey }: { classificationKey: string }) => (
  <span className="tool-chip-icon">
    <img alt="" height="12" src={`${EXTRA_SMALL_ICON_BASE_SRC}/tool-${classificationKey}-color.svg`} width="12" />
  </span>
)

export const AssignedToCell = ({ countText, pack, note }: { countText: React.ReactNode, pack: Pack, note: React.ReactNode }) => (
  <React.Fragment>
    <span className="assigned-count-row">
      <span>{countText}</span>
      {canManagePack(pack) && (
        <a className="quill-button extra-small outlined grey focus-on-light" href={manageStudentsLink(pack.unitId)}>Edit students</a>
      )}
    </span>
    {note}
  </React.Fragment>
)

export const DetailRows = ({ activitiesContent, activitiesLabel, assignedOn, assignedTo, customPackName, lastActive, packNameHint, shownParts = ALL_CARD_PARTS, toolChips, toolLabel }: {
  activitiesContent: React.ReactNode,
  activitiesLabel: string,
  assignedOn: React.ReactNode,
  assignedTo: React.ReactNode,
  customPackName?: string | null,
  lastActive: React.ReactNode,
  packNameHint: string,
  shownParts?: CardPart[],
  toolChips: React.ReactNode,
  toolLabel: string,
}) => (
  <React.Fragment>
    <div aria-hidden="true" className="detail-divider details-top-divider" />
    <h4>Details</h4>
    <dl className="detail-grid">
      {customPackName && (
        <React.Fragment>
          <div className="detail-row pack-name-row">
            <dt>
              <DetailLabelHint explanation={packNameHint} label="Pack name" />
            </dt>
            <dd>{customPackName}</dd>
          </div>
          <div aria-hidden="true" className="detail-divider pack-name-divider" />
        </React.Fragment>
      )}
      {shownParts.includes(ASSIGNED_ON) && (
        <div className="detail-row assigned-on-row">
          <dt>Assigned on</dt>
          <dd>{assignedOn}</dd>
        </div>
      )}
      {shownParts.includes(LAST_ACTIVE) && (
        <div className="detail-row last-active-row">
          <dt>Last active</dt>
          <dd>{lastActive}</dd>
        </div>
      )}
      <div className="detail-row assigned-to-row">
        <dt>Assigned to</dt>
        <dd>{assignedTo}</dd>
      </div>
      {shownParts.includes(LEARNING_TOOLS) && (
        <React.Fragment>
          <div aria-hidden="true" className="detail-divider before-learning-tool-divider" />
          <div className="detail-row learning-tool-row">
            <dt>{toolLabel}</dt>
            <dd>{toolChips}</dd>
          </div>
        </React.Fragment>
      )}
      <div aria-hidden="true" className="detail-divider before-activities-divider" />
      <div className="detail-row activities-row">
        <dt>{activitiesLabel}</dt>
        <dd>{activitiesContent}</dd>
      </div>
    </dl>
  </React.Fragment>
)

const NBSP = '\u00a0'

// non-breaking spaces keep each date whole, so "Publishes Oct 27" never splits across lines
const keepTogether = (text: string) => text.replace(/ /g, NBSP)

export function activityDateParts({ scheduled, publishDate, dueDate }: { scheduled?: boolean, publishDate?: string, dueDate?: string }): string[] {
  const publishes = scheduled ? formatShortDate(publishDate) : null
  const due = formatShortDate(dueDate)
  const scheduledText = scheduled && (publishes ? `Publishes ${publishes}` : 'Scheduled')
  return [scheduledText, due && `Due ${due}`].filter(Boolean).map(keepTogether)
}

export const ToolChip = ({ classificationId, text, href, isActivity, scheduled, publishDate, dueDate }: { classificationId: number, text?: string, href?: string, isActivity?: boolean, scheduled?: boolean, publishDate?: string, dueDate?: string }) => {
  const decodedText = React.useMemo(() => (text ? safeStripHtml(text) || text : ''), [text])
  const labelRef = React.useRef<HTMLSpanElement>(null)
  const [title, setTitle] = React.useState<string>()

  const classification = CLASSIFICATIONS[Number(classificationId)]
  if (!classification) { return null }

  const label = decodedText || classification.name
  const dateParts = isActivity ? activityDateParts({ scheduled, publishDate, dueDate }) : []
  const className = `tool-chip${href ? ' focus-on-light' : ''}`

  // only a truncated name gets a tooltip, so screen readers don't hear every name twice
  function showFullNameIfTruncated() {
    const labelElement = labelRef.current
    setTitle(labelElement && labelElement.scrollWidth > labelElement.clientWidth ? label : undefined)
  }

  const content = (
    <React.Fragment>
      <span className="tool-chip-name">
        <ToolChipIcon classificationKey={classification.key} />
        <span className="tool-chip-label" ref={labelRef}>{label}</span>
      </span>
      {dateParts.length > 0 && (
        <span className="tool-chip-dates">
          {dateParts.map((part, index) => (
            <span className="tool-chip-date" key={part}>
              <span className="sr-only">, </span>
              {index > 0 && <span aria-hidden="true" className="tool-chip-date-separator">{' · '}</span>}
              {part}
            </span>
          ))}
        </span>
      )}
    </React.Fragment>
  )

  return href ? (
    <a className={className} href={href} onMouseEnter={showFullNameIfTruncated} rel="noopener noreferrer" target="_blank" title={title}>
      {content}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : (
    <span className={className}>{content}</span>
  )
}

export const activityPreviewPath = (activityId: string | number) => `/activity_sessions/anonymous?activity_id=${activityId}`

export const DetailLabelHint = ({ label, explanation }: { label: string, explanation: string }) => (
  <Tooltip
    isTabbable={false}
    tooltipText={explanation}
    tooltipTriggerText={<span aria-label={explanation} className="detail-label-hint" role="img">{label}</span>}
  />
)

export const learningToolCount = (pack: Pack) => new Set(pack.activities.map(a => Number(a.classificationId))).size

export const learningToolLabel = (pack: Pack) => pluralize(learningToolCount(pack), 'Learning tool', 'Learning tools')

export const LearningToolChips = ({ pack }: { pack: Pack }) => {
  const classificationIds = Array.from(new Set(pack.activities.map(a => Number(a.classificationId))))
  return (
    <div className="chip-row learning-tool-chips">
      {classificationIds.map(id => <ToolChip classificationId={id} key={id} />)}
    </div>
  )
}

const expandChevron = (isExpanded: boolean) => (
  <img alt="" height="12" src={`${EXTRA_SMALL_ICON_BASE_SRC}/chevron-down.svg`} style={isExpanded ? { transform: 'rotate(180deg)' } : undefined} width="12" />
)

function activitiesToggleLabel(activityCount: number, isExpanded: boolean, isCollapsible: boolean) {
  if (isCollapsible) { return <React.Fragment>{isExpanded ? 'Hide' : 'Show'}<span className="sr-only"> activities</span></React.Fragment> }
  return isExpanded ? 'Show fewer activities' : `Show all ${activityCount} activities`
}

export const ActivityChips = ({ activities, isCollapsible = false, searchQuery = '' }: { activities: PackActivity[], isCollapsible?: boolean, searchQuery?: string }) => {
  const opensForSearch = isCollapsible && activities.some(activity => nameMatchesSearch(activity.name, searchQuery))
  const [isExpanded, setIsExpanded] = React.useState(opensForSearch)

  React.useEffect(() => { setIsExpanded(opensForSearch) }, [opensForSearch])

  function toggleExpanded() { setIsExpanded(!isExpanded) }

  const showsList = !isCollapsible || isExpanded
  const visible = isCollapsible || isExpanded ? activities : activities.slice(0, VISIBLE_ACTIVITY_COUNT)
  const hasToggle = isCollapsible || activities.length > VISIBLE_ACTIVITY_COUNT

  return (
    <div className="activity-chips">
      {showsList && (
        <ul className="chip-row">
          {visible.map(activity => (
            <li key={activity.activityId || activity.name}>
              <ToolChip classificationId={Number(activity.classificationId)} dueDate={activity.dueDate} href={activity.activityId ? activityPreviewPath(activity.activityId) : undefined} isActivity={true} publishDate={activity.publishDate} scheduled={activity.scheduled} text={activity.name} />
            </li>
          ))}
        </ul>
      )}
      {hasToggle && (
        <button
          aria-expanded={isExpanded}
          className={`quill-button extra-small outlined grey show-all-activities focus-on-light${isCollapsible ? ' activities-disclosure' : ''}`}
          onClick={toggleExpanded}
          type="button"
        >
          {activitiesToggleLabel(activities.length, isExpanded, isCollapsible)}
          {expandChevron(isExpanded)}
        </button>
      )}
    </div>
  )
}

export const AssignedElsewhereNote = ({ classrooms, selectedClassroomId, isDiagnostic }: { classrooms: PackClassroom[], selectedClassroomId: string | number, isDiagnostic?: boolean }) => {
  if (selectedClassroomId === ALL) { return null }
  const otherClasses = assignedClassrooms(classrooms).filter(classroom => String(classroom.id) !== String(selectedClassroomId))
  if (!otherClasses.length) { return null }

  const currentClass = selectedClassroomFor(classrooms, selectedClassroomId)
  const currentSentence = currentClass ? classAssignmentSentence(currentClass, isDiagnostic) : ''
  const otherLines = otherClasses.map(classAssignmentLine)
  const heading = "It's also assigned to:"
  const currentHtml = currentSentence ? `<span class="tooltip-lead">${escapeHtml(currentSentence)}</span>` : ''
  const tooltipHtml = `${currentHtml}<span class="assigned-elsewhere-heading">${escapeHtml(heading)}</span>${otherLines.map(escapeHtml).join('<br>')}`
  const label = `Also assigned to ${otherClasses.length} other ${pluralize(otherClasses.length, 'class', 'classes')}`
  const ariaLabel = [`${label}.`, currentSentence, `${heading} ${otherLines.join('; ')}`].filter(Boolean).join(' ')

  return (
    <div className="assigned-elsewhere">
      <Tooltip
        isTabbable={false}
        tooltipText={tooltipHtml}
        tooltipTriggerText={(
          <span aria-label={ariaLabel} className="assigned-elsewhere-trigger" role="img">{label}</span>
        )}
      />
    </div>
  )
}

const PackDetails = ({ pack, progress, selectedClassroomId, customPackName, onManageActivities, shownParts, collapsesActivities, activitySearch }: { pack: Pack, progress: AggregatedProgress, selectedClassroomId: string | number, customPackName?: string | null, onManageActivities: () => void, shownParts: CardPart[], collapsesActivities?: boolean, activitySearch?: string }) => {
  const relevantClassrooms = selectedClassroomId === ALL
    ? pack.classrooms
    : pack.classrooms.filter(c => String(c.id) === String(selectedClassroomId))
  const classroomCount = assignedClassrooms(relevantClassrooms).length
  const studentCount = progress.assigned || relevantClassrooms.reduce((sum, c) => sum + Number(c.assignedStudentCount || 0), 0)
  const rosterCount = rosterCountFor(pack, selectedClassroomId)
  const assignedDisplay = rosterCount ? Math.min(studentCount, rosterCount) : studentCount
  const toolCount = learningToolCount(pack)

  return (
    <div className="pack-card-details">
      <DetailRows
        activitiesContent={(
          <ActivitiesCell onManageActivities={onManageActivities} pack={pack}>
            <ActivityChips activities={pack.activities} isCollapsible={collapsesActivities} searchQuery={activitySearch} />
          </ActivitiesCell>
        )}
        activitiesLabel={`${pack.activities.length} ${pluralize(pack.activities.length, 'activity', 'activities')}`}
        assignedOn={formatDate(packAssignedEpochFor(pack, selectedClassroomId) * 1000)}
        assignedTo={(
          <AssignedToCell
            countText={(
              <React.Fragment>
                {selectedClassroomId === ALL && <span>{classroomCount} {pluralize(classroomCount, 'class', 'classes')},&nbsp;</span>}
                {rosterCount ? `${assignedDisplay} of ${rosterCount}` : studentCount} {pluralize(rosterCount || studentCount, 'student', 'students')}
              </React.Fragment>
            )}
            note={<AssignedElsewhereNote classrooms={pack.classrooms} selectedClassroomId={selectedClassroomId} />}
            pack={pack}
          />
        )}
        customPackName={customPackName}
        lastActive={progress.lastActive ? formatDate(progress.lastActive) : '—'}
        packNameHint="The custom name you gave this pack. It's what students see on their dashboards. The card's title stays the pack's original name so it's easy to recognize here."
        shownParts={shownParts}
        toolChips={<LearningToolChips pack={pack} />}
        toolLabel={pluralize(toolCount, 'Learning tool', 'Learning tools')}
      />
    </div>
  )
}

const PackCard = ({ pack, unitProgress, selectedClassroomId, onPackChanged, isBoosted, onToggleBoost, parentDiagnosticHref, parentDiagnosticLabel, isCompact, activitySearch, shownParts = ALL_CARD_PARTS }: PackCardProps) => {
  const [showModal, setShowModal] = React.useState('')
  const progress = aggregateProgress(pack, unitProgress, selectedClassroomId)

  function openManageActivities() { setShowModal(EDIT_ACTIVITIES) }

  const students = studentCompletionsFor(unitProgress, selectedClassroomId)
  const classroomName = selectedClassroomFor(pack.classrooms, selectedClassroomId)?.name
  const progressHeading = students.length
    ? `${Math.round((completedCountFor(students) / students.length) * 100)}% complete`
    : 'Progress'

  function reportHrefFor(student: StudentCompletion) {
    const hasFinishedWork = student.completed || (student.progress_pct ?? 0) > 0
    return selectedClassroomId === ALL || !hasFinishedWork ? undefined : studentOverviewLink(selectedClassroomId, student.id, pack.unitId)
  }
  const badges = shownParts.includes(STUDENTS) && <StudentCompletionBadges reportHrefFor={reportHrefFor} showsNames={!isCompact} students={students} />
  const progressVisual = students.length ? badges : (<p className="no-students">There are no students in this class yet.</p>)
  const analysisClassroomId = selectedClassroomId === ALL ? pack.classrooms[0]?.id : selectedClassroomId
  const buttons = progress.completedAny > 0 ? (
    <a
      className="quill-button contained extra-small green focus-on-light"
      href={`/teachers/progress_reports/diagnostic_reports/activity_packs?classroom_id=${analysisClassroomId}#unit-${pack.unitId}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      View results<span className="sr-only"> (opens in a new window)</span>
    </a>
  ) : (
    <Tooltip
      isTabbable={false}
      tooltipText="No students have completed an activity yet."
      tooltipTriggerText={
        <button aria-disabled="true" aria-label="View results. No students have completed an activity yet." className="quill-button contained extra-small disabled focus-on-light" type="button">View results</button>
      }
    />
  )

  const fromRecommendations = Boolean(unitProgress?.from_recommendations)
  const recDefaultName = unitProgress?.template_name
  const isRenamed = Boolean(recDefaultName) && !isDefaultPackName(pack.unitName, recDefaultName)
  const { title, parentDiagnostic } = splitRecommendationName(
    fromRecommendations && isRenamed && recDefaultName ? recDefaultName : pack.unitName,
    fromRecommendations
  )
  const customPackName = fromRecommendations && isRenamed ? pack.unitName : null
  const parent = parentDiagnostic ? parseParentDiagnostic(parentDiagnostic) : null
  const parentLabel = parentDiagnosticLabel
    || (parent ? `${parent.family}${parent.phase ? ` · ${parent.phase === 'Post' ? 'Growth Post' : 'Baseline Pre'}` : ''}` : null)

  return (
    <section className={`pack-card ${isBoosted ? 'boosted' : ''}`} data-card-id={packCardId(pack.unitId)}>
      <div className={`pack-card-header ${parentLabel ? 'has-lineage' : ''}`}>
        {parentLabel && (
          <div className="pack-parent-branch">
            <span className="parent-name">{parentLabel}</span>
          </div>
        )}
        <h3 title={pack.unitName}>{title}</h3>
        <PackActions isBoosted={isBoosted} onPackChanged={onPackChanged} onToggleBoost={onToggleBoost} pack={pack} selectedClassroomId={selectedClassroomId} setShowModal={setShowModal} showModal={showModal} />
      </div>
      <div className="pack-card-body">
        <div className="pack-card-progress">
          <div className="progress-heading">
            <h4>{progressHeading}</h4>
            {students.length > 0 && <ProgressHelp assigned={students.length} classroomName={classroomName} completed={completedCountFor(students)} pack={pack} roster={rosterCountFor(pack, selectedClassroomId)} />}
          </div>
          {progressVisual}
          <div className="pack-card-buttons">
            {buttons}
            {parentDiagnosticHref && (
              <a className="quill-button outlined extra-small green focus-on-light" href={parentDiagnosticHref} rel="noopener noreferrer" target="_blank">
                View diagnostic<span className="sr-only"> (opens in a new window)</span>
              </a>
            )}
          </div>
        </div>
        <PackDetails activitySearch={activitySearch} collapsesActivities={isCompact} customPackName={customPackName} onManageActivities={openManageActivities} pack={pack} progress={progress} selectedClassroomId={selectedClassroomId} shownParts={shownParts} />
      </div>
    </section>
  )
}

export default PackCard
